import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import ReactMarkdown from "react-markdown";
import { Loader2, Sparkles } from "lucide-react";

import { CITY_CATALOG } from "@/lib/tour-search";
import type { ConciergeTourCard } from "@/lib/concierge-types";

const INTERESTS = ["Playa", "Cultura", "Aventura", "Gastronomía", "Naturaleza", "Relax", "Familia", "Vida nocturna"];

export const Route = createFileRoute("/disena-mi-viaje")({
  head: () => ({
    meta: [
      { title: "Diseña tu viaje con IA · Mostrando México" },
      {
        name: "description",
        content: "Itinerario personalizado a México creado por Maya, nuestra Concierge IA, usando tours reales.",
      },
      { property: "og:title", content: "Diseña tu viaje con IA · Mostrando México" },
    ],
    links: [{ rel: "canonical", href: "https://www.mostrandomexico.mx/disena-mi-viaje" }],
  }),
  component: DesignTripPage,
});

function DesignTripPage() {
  const [city, setCity] = useState("cancun");
  const [days, setDays] = useState(5);
  const [budget, setBudget] = useState(1500);
  const [adults, setAdults] = useState(2);
  const [kids, setKids] = useState(0);
  const [picked, setPicked] = useState<string[]>(["Playa", "Cultura"]);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<{ message: string; cards: ConciergeTourCard[] } | null>(null);

  async function generate() {
    setLoading(true);
    setResult(null);
    const prompt = `Diseña un itinerario detallado de ${days} días en ${CITY_CATALOG[city]?.name ?? city} para ${adults} adultos y ${kids} niños, presupuesto aproximado ${budget} USD por persona. Intereses: ${picked.join(", ")}. Usa la tool build_itinerary y luego calc_budget. Da una recomendación final con tono cálido.`;
    try {
      const resp = await fetch("/api/concierge", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          messages: [{ role: "user", content: prompt }],
          context: { page: "/disena-mi-viaje", city },
        }),
      });
      const data = await resp.json();
      if (!resp.ok) throw new Error(data?.error || "Error");
      setResult({ message: data.message, cards: data.cards || [] });
    } catch (e: any) {
      setResult({ message: `❌ ${e?.message || "No se pudo generar."}`, cards: [] });
    } finally {
      setLoading(false);
    }
  }

  return (
    <main className="mx-auto max-w-4xl px-4 py-10 md:py-16">
      <header className="text-center mb-10">
        <div className="inline-flex items-center gap-2 rounded-full border bg-accent/40 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-accent-foreground">
          <Sparkles className="h-3 w-3" /> Modo Premium
        </div>
        <h1 className="mt-4 text-4xl font-bold tracking-tight md:text-5xl">Diseña tu viaje con Maya IA</h1>
        <p className="mt-3 text-muted-foreground max-w-xl mx-auto">
          Cuéntanos lo que buscas y nuestra Concierge IA arma un itinerario personalizado usando tours reales de Mostrando México.
        </p>
      </header>

      <section className="grid gap-5 rounded-2xl border bg-card p-6 shadow-sm md:p-8">
        <div className="grid gap-4 md:grid-cols-2">
          <Field label="Destino">
            <select
              value={city}
              onChange={(e) => setCity(e.target.value)}
              className="w-full rounded-lg border bg-background px-3 py-2"
            >
              {Object.entries(CITY_CATALOG).map(([slug, c]) => (
                <option key={slug} value={slug}>
                  {c.name}
                </option>
              ))}
            </select>
          </Field>
          <Field label={`Duración: ${days} días`}>
            <input type="range" min={1} max={14} value={days} onChange={(e) => setDays(+e.target.value)} className="w-full" />
          </Field>
          <Field label={`Presupuesto por persona: $${budget} USD`}>
            <input type="range" min={200} max={5000} step={50} value={budget} onChange={(e) => setBudget(+e.target.value)} className="w-full" />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Adultos">
              <input type="number" min={1} value={adults} onChange={(e) => setAdults(+e.target.value)} className="w-full rounded-lg border bg-background px-3 py-2" />
            </Field>
            <Field label="Niños">
              <input type="number" min={0} value={kids} onChange={(e) => setKids(+e.target.value)} className="w-full rounded-lg border bg-background px-3 py-2" />
            </Field>
          </div>
        </div>

        <Field label="Intereses">
          <div className="flex flex-wrap gap-2">
            {INTERESTS.map((i) => {
              const active = picked.includes(i);
              return (
                <button
                  key={i}
                  type="button"
                  onClick={() => setPicked((p) => (active ? p.filter((x) => x !== i) : [...p, i]))}
                  className={`rounded-full border px-3 py-1.5 text-sm transition ${
                    active ? "bg-primary text-primary-foreground border-primary" : "bg-background hover:bg-accent"
                  }`}
                >
                  {i}
                </button>
              );
            })}
          </div>
        </Field>

        <button
          onClick={generate}
          disabled={loading}
          className="mt-2 inline-flex items-center justify-center gap-2 rounded-full bg-primary px-6 py-3 font-semibold text-primary-foreground shadow-lg hover:bg-primary/90 disabled:opacity-60"
        >
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
          {loading ? "Maya está diseñando tu viaje…" : "Generar itinerario con IA"}
        </button>
      </section>

      {result && (
        <section className="mt-10 space-y-6">
          <article className="rounded-2xl border bg-card p-6 prose prose-sm max-w-none md:p-8">
            <ReactMarkdown>{result.message}</ReactMarkdown>
          </article>
          {result.cards.length > 0 && (
            <div className="grid gap-4 sm:grid-cols-2">
              {result.cards.map((c) => (
                <a key={c.id} href={c.url} className="flex gap-3 rounded-xl border bg-card p-3 hover:border-primary hover:shadow-md transition">
                  {c.image && <img loading="lazy" decoding="async" src={c.image} alt={c.title} className="h-20 w-20 rounded-lg object-cover flex-shrink-0" />}
                  <div className="min-w-0">
                    <p className="text-sm font-semibold line-clamp-2">{c.title}</p>
                    <p className="text-xs text-muted-foreground">{c.location}</p>
                    <p className="text-sm font-bold text-primary mt-1">
                      {c.priceFrom != null ? `Desde ${c.priceFrom} ${c.currency ?? ""}` : "Consultar"}
                    </p>
                  </div>
                </a>
              ))}
            </div>
          )}
        </section>
      )}
    </main>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block text-sm">
      <span className="mb-1.5 block font-medium text-foreground">{label}</span>
      {children}
    </label>
  );
}
